import { useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import api from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "@/components/StatusBadge";
import { useAuth } from "@/context/AuthContext";
import { Plus, Search, Folder } from "lucide-react";
import { toast } from "sonner";

const STATUS_OPTIONS = ["draft", "active", "paused", "archived"];

export default function Projects() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ client_name: "", name: "", description: "", test_scope: "", status: "active" });
  const [templates, setTemplates] = useState([]);
  const [chosenTemplate, setChosenTemplate] = useState("");
  const nav = useNavigate();

  const load = async () => {
    const { data } = await api.get("/projects");
    setProjects(data || []);
  };
  const loadTemplates = async () => {
    try { const { data } = await api.get("/project-templates"); setTemplates(data || []); }
    catch { /* freelancers don't have access */ }
  };
  useEffect(() => {
    load(); loadTemplates();
    // Refresh whenever the admin refocuses the tab / comes back to Projects. Some browsers
    // fire BOTH focus and visibilitychange on return, so listen only to visibilitychange
    // (fires reliably on tab-switch, mobile background, etc.).
    const onVisibility = () => { if (document.visibilityState === "visible") load(); };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  const filtered = projects.filter(p => !q || (p.name + " " + p.client_name).toLowerCase().includes(q.toLowerCase()));

  const canEditStatus = user?.role === "admin" || user?.role === "manager";

  const changeStatus = async (pid, newStatus) => {
    try {
      await api.patch(`/projects/${pid}`, { status: newStatus });
      setProjects(prev => prev.map(pr => pr.id === pid ? { ...pr, status: newStatus } : pr));
      toast.success(`Project marked ${newStatus}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update status");
    }
  };

  const create = async () => {
    try {
      const { data: created } = await api.post("/projects", form);
      if (chosenTemplate && created?.id) {
        try {
          const { data: applied } = await api.post(`/projects/${created.id}/apply-template`, { template_id: chosenTemplate });
          toast.success(`Project created — ${applied.template} added (${applied.tasks_created.length} starter tasks)`);
        } catch (e) {
          toast.warning("Project created but template could not be applied");
        }
      } else {
        toast.success("Project created");
      }
      setOpen(false);
      setForm({ client_name: "", name: "", description: "", test_scope: "", status: "active" });
      setChosenTemplate("");
      load();
    } catch (e) { toast.error("Failed"); }
  };

  return (
    <AppLayout title="Projects" subtitle="Workspaces">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div className="relative w-full sm:max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Search projects" value={q} onChange={e=>setQ(e.target.value)} className="pl-9" autoCapitalize="none" autoCorrect="off" data-testid="input-search" />
        </div>
        {(user.role === "admin" || user.role === "manager") && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-slate-900 hover:bg-slate-800" data-testid="btn-new-project"><Plus className="w-4 h-4 mr-1.5" /> New project</Button>
            </DialogTrigger>
            <DialogContent className="max-h-[85vh] overflow-auto sm:max-w-lg">
              <DialogHeader><DialogTitle>Create project</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Client name</Label><Input value={form.client_name} onChange={e=>setForm(f=>({...f, client_name:e.target.value}))} data-testid="input-client" /></div>
                <div><Label>Project name</Label><Input value={form.name} onChange={e=>setForm(f=>({...f, name:e.target.value}))} data-testid="input-project-name" /></div>
                <div><Label>Description</Label><Textarea value={form.description} onChange={e=>setForm(f=>({...f, description:e.target.value}))} data-testid="input-description" /></div>
                <div><Label>Test scope</Label><Textarea value={form.test_scope} onChange={e=>setForm(f=>({...f, test_scope:e.target.value}))} data-testid="input-scope" /></div>
                <div>
                  <Label>Status</Label>
                  <Select value={form.status} onValueChange={(v)=>setForm(f=>({...f, status:v}))}>
                    <SelectTrigger data-testid="select-status"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      {["draft","active","paused","completed","archived"].map(s=> <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {templates.length > 0 && (
                  <div className="border-t pt-3 mt-1">
                    <Label className="flex items-center justify-between">
                      <span>Workflow template <span className="text-xs font-normal text-slate-500">(optional)</span></span>
                      {chosenTemplate && <button type="button" onClick={()=>setChosenTemplate("")} className="text-[11px] text-slate-500 hover:text-red-600" data-testid="btn-clear-template">Clear</button>}
                    </Label>
                    <div className="grid grid-cols-2 gap-2 mt-2" data-testid="template-grid">
                      {templates.map(t => (
                        <button
                          key={t.id}
                          type="button"
                          onClick={()=>setChosenTemplate(prev => prev === t.id ? "" : t.id)}
                          className={`text-left rounded border p-2.5 transition ${chosenTemplate === t.id ? "border-cyan-500 bg-cyan-50" : "border-slate-200 hover:border-slate-400"}`}
                          data-testid={`template-card-${t.id}`}
                        >
                          <div className="text-xs font-semibold text-slate-900">{t.name}</div>
                          <div className="text-[11px] text-slate-500 mt-0.5 line-clamp-2">{t.description}</div>
                          <div className="text-[10px] text-slate-400 mt-1.5">{t.task_count} tasks · {t.total_hours}h · ~${t.estimated_payout}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button onClick={create} className="bg-cyan-600 hover:bg-cyan-700" data-testid="btn-create-project">Create</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map(p => (
          <div key={p.id} className="kpi-card cursor-pointer hover:border-cyan-300 transition-colors" onClick={()=>nav(`/projects/${p.id}`)} data-testid={`card-project-${p.id}`}>
            <div className="flex items-start justify-between">
              <div className="min-w-0">
                <div className="text-[11px] text-slate-500 uppercase tracking-wider truncate">{p.client_name}</div>
                <h3 className="font-display font-bold text-slate-900 mt-0.5 truncate">{p.name}</h3>
              </div>
            </div>
            <p className="text-sm text-slate-600 mt-3 line-clamp-2">{p.description || "—"}</p>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500 mt-3 pt-3 border-t border-slate-100" data-testid={`stats-project-${p.id}`}>
              <span><b className="text-slate-900" data-testid={`stat-tasks-${p.id}`}>{p.task_count}</b> tasks</span>
              <span>·</span>
              <span><b className="text-slate-900" data-testid={`stat-open-${p.id}`}>{p.open_tasks}</b> open</span>
              <span>·</span>
              <span title="Accepted testers"><b className="text-emerald-700" data-testid={`stat-testers-${p.id}`}>{p.active_testers ?? 0}</b> tester{(p.active_testers ?? 0) === 1 ? "" : "s"}</span>
              {(p.pending_invitations ?? 0) > 0 && (
                <>
                  <span>·</span>
                  <span title="Pending invitations" className="text-amber-700"><b data-testid={`stat-pending-${p.id}`}>{p.pending_invitations}</b> pending</span>
                </>
              )}
            </div>
            <div className="flex items-center justify-end mt-3">
              {canEditStatus ? (
                <div onClick={(e)=>e.stopPropagation()}>
                  {(() => {
                    // Radix requires the current value to match a rendered SelectItem —
                    // otherwise the trigger crashes. Compose the dropdown options as the
                    // base list PLUS the project's current status if it's not already in
                    // it (e.g. legacy 'completed' projects after we hid the option).
                    const opts = STATUS_OPTIONS.includes(p.status) || !p.status
                      ? STATUS_OPTIONS
                      : [...STATUS_OPTIONS, p.status];
                    return (
                      <Select value={p.status || "draft"} onValueChange={(v)=>changeStatus(p.id, v)}>
                        <SelectTrigger className="h-7 w-32 text-xs" data-testid={`select-status-${p.id}`}>
                          <SelectValue placeholder="Status"/>
                        </SelectTrigger>
                        <SelectContent>
                          {opts.map(s => (
                            <SelectItem key={s} value={s} data-testid={`opt-status-${p.id}-${s}`}>
                              {s.charAt(0).toUpperCase() + s.slice(1)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    );
                  })()}
                </div>
              ) : (
                <StatusBadge kind="task" value={p.status} />
              )}
            </div>
          </div>
        ))}
        {filtered.length === 0 && <div className="col-span-full text-center py-12 text-slate-500"><Folder className="w-8 h-8 mx-auto mb-2"/>No projects yet.</div>}
      </div>
    </AppLayout>
  );
}
